import { getLocalTimeZone, now, parseAbsolute } from "@internationalized/date"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ContactType } from "ipctg-inflow-types/InFlowEnums"
import { useEffect } from "react"
import { Trans, useTranslation } from "react-i18next"

import { LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Text from "@/globalComponents/atoms/Text"
import DoorAccessCodeCard from "@/globalComponents/molecules/DoorAccessCodeCard"
import PurchasedSingleSessionCard from "@/globalComponents/molecules/productCards/PurchasedSingleSessionCard"
import {
  confirmParkPaymentQueryOptions,
  parkReceiptQueryOptions,
} from "@/utilities/hooks/queries/park"
import { useTheme } from "@/utilities/hooks/useTheme"
import { formatPhoneNumberForDisplay } from "@/utilities/phone"

export const Route = createFileRoute(
  "/qr/$qrLookupID/park/$epoch/confirmation"
)({
  staticData: { pageName: "Confirmation" },

  beforeLoad: async ({ context }) => {
    const { sessionState, queryClient } = context

    // Backend now handles idempotency via PAYMENTAPIRESPONSE table
    const payRsp = await queryClient.fetchQuery(
      confirmParkPaymentQueryOptions()
    )

    // Fetch receipt data and return it directly
    const { receipt } = await queryClient.fetchQuery(
      parkReceiptQueryOptions({ receiptID: payRsp.receiptID })
    )

    // Determine contact info to display
    const contactInfo =
      sessionState.contactType === ContactType.Email
        ? sessionState.email
        : formatPhoneNumberForDisplay(sessionState.phoneNumber || "")

    return {
      receipt,
      contactInfo,
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { qrLookupID } = Route.useParams()
  const { receipt, contactInfo } = Route.useRouteContext()
  const { locationInformation } = useTheme()
  const { t } = useTranslation("park")
  const navigate = Route.useNavigate()

  const locationName = locationInformation?.locationName
  const sessionStartTime = parseAbsolute(receipt.startTime, getLocalTimeZone())
  const sessionEndTime = parseAbsolute(receipt.endTime, getLocalTimeZone())

  // Navigate the user to receipt once their session has ended
  useEffect(() => {
    const endtime = sessionEndTime
    const currentTime = now(getLocalTimeZone())
    const difference = endtime.compare(currentTime)

    const timeout = setTimeout(
      () => {
        navigate({
          to: "/qr/$qrLookupID/receipt/$receiptID",
          params: { qrLookupID, receiptID: receipt.receiptID },
          replace: true,
        })
      },
      Math.max(0, difference)
    )

    return () => clearTimeout(timeout)
  }, [navigate, qrLookupID, sessionEndTime, receipt.receiptID])

  return (
    <>
      <Heading level={1} className="center">
        {t("confirmation.header")}
      </Heading>
      <Text level={1} className="center">
        {locationName
          ? t("confirmation.description.withLocation", {
              locationName,
            })
          : t("confirmation.description.withoutLocation")}
      </Text>
      <section className="module">
        {receipt.accessCode ? (
          <DoorAccessCodeCard>{receipt.accessCode}</DoorAccessCodeCard>
        ) : (
          <></>
        )}
        <PurchasedSingleSessionCard
          receiptID={receipt.receiptID}
          licensePlate={receipt.plate}
          sessionStartTime={sessionStartTime}
          sessionEndTime={sessionEndTime}
        />
        <Text
          className="center"
          style={{ width: "100%", overflowWrap: "break-word" }}
        >
          <Trans
            ns="receipt"
            i18nKey="sentNotification"
            values={{ contact: contactInfo }}
          >
            Your receipt has been sent to: <br />
            <em className="semi-bold" style={{ wordBreak: "break-word" }}>
              {contactInfo}
            </em>
          </Trans>
        </Text>
        <LinkButton
          buttonType="primary"
          presentation="filled"
          to="/qr/$qrLookupID/receipt/$receiptID"
          params={{ qrLookupID, receiptID: receipt.receiptID }}
        >
          <Icon name="receipt" slot="left" />
          {t("viewReceipt", { ns: "receipt" })}
        </LinkButton>
      </section>
      <Link
        to="/qr/$qrLookupID/welcome"
        params={{ qrLookupID }}
        className="center semi-bold body--2"
      >
        {t("common.returnToPurchaseMenu", { ns: "common" })}
      </Link>
    </>
  )
}
